import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import { Clock, CreditCard, Lock, Receipt, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { DateRangeInputs } from "@libs/rubick";
import InvoiceCard from "@/components/invoices/InvoiceCard";
import InvoiceDetailModal from "@/components/invoices/InvoiceDetailModal";
import InvoiceFilters from "@/components/invoices/InvoiceFilters";
import { Card } from "@/components/ui/Card";
import { INVOICE_STATUSES, INVOICE_STATUS_LABELS } from "@/constants/invoices";
import { useInvoices } from "@/hooks/useInvoices";
import { usePermissions } from "@/hooks/usePermissions";
import { useEffectiveCustomerId } from "@/hooks/useEffectiveCustomerId";
import { DashboardPageLayout, DashboardPageLoading, DashboardPageSection } from "@/layouts/DashboardPageLayout";
import { formatDateInput, parseDateInput } from "@/utils/date";
import { formatCurrency } from "@/utils/formatters";
import { getHttpResponseMessage } from "@/utils/httpError";
function getMonthRange(monthOffset) {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + monthOffset, 1));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + monthOffset + 1, 0));
    return {
        startDate: formatDateInput(start),
        endDate: formatDateInput(end)
    };
}
export default function InvoicesPage() {
    const { canViewInvoices } = usePermissions();
    const { isSuperAdmin, isViewingAllOrgs, selectedCustomerId } = useEffectiveCustomerId();
    const invoiceCustomerId = isSuperAdmin && !isViewingAllOrgs ? selectedCustomerId : undefined;
    const currentMonthRange = getMonthRange(0);
    const [filters, setFilters] = useState({});
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
    const [manualPeriodStart, setManualPeriodStart] = useState(currentMonthRange.startDate);
    const [manualPeriodEnd, setManualPeriodEnd] = useState(currentMonthRange.endDate);
    const { invoices, isLoading, error, refetch, getTotals, summaryCurrency, updateStatus, download, generate } = useInvoices(filters);
    const selectedInvoice = useMemo(()=>invoices.find((i)=>i.invoiceId === selectedInvoiceId) ?? null, [
        invoices,
        selectedInvoiceId
    ]);
    const totals = getTotals();
    const lastErrorMessageRef = useRef(null);
    const handleFiltersChange = (newFilters)=>{
        setFilters(newFilters);
    };
    const handleCloseModal = ()=>{
        setSelectedInvoiceId(null);
    };
    const handleRefresh = async ()=>{
        try {
            await refetch();
            toast.success("Invoices refreshed");
        } catch  {
            toast.error("Failed to refresh invoices");
        }
    };
    const applyMonthPreset = (monthOffset)=>{
        const { startDate, endDate } = getMonthRange(monthOffset);
        setManualPeriodStart(startDate);
        setManualPeriodEnd(endDate);
    };
    const handleManualStartDateChange = (date)=>{
        setManualPeriodStart(date ? formatDateInput(date) : "");
    };
    const handleManualEndDateChange = (date)=>{
        setManualPeriodEnd(date ? formatDateInput(date) : "");
    };
    const handleManualGenerate = ()=>{
        if (!invoiceCustomerId) {
            toast.error("Select an organization before generating invoices");
            return;
        }
        if (!manualPeriodStart || !manualPeriodEnd) {
            toast.error("Select both a start and end date");
            return;
        }
        if (manualPeriodStart > manualPeriodEnd) {
            toast.error("Start date must be on or before end date");
            return;
        }
        generate.mutate({
            customerId: invoiceCustomerId,
            periodStart: manualPeriodStart,
            periodEnd: manualPeriodEnd,
            notes: `Manual billing run for ${manualPeriodStart} to ${manualPeriodEnd}`
        });
    };
    useEffect(()=>{
        if (!error) {
            lastErrorMessageRef.current = null;
            return;
        }
        const message = getHttpResponseMessage(error) || "Failed to load invoices";
        if (lastErrorMessageRef.current === message) {
            return;
        }
        toast.error(message);
        lastErrorMessageRef.current = message;
    }, [
        error
    ]);
    if (isLoading && invoices.length === 0) {
        return /*#__PURE__*/ _jsx(DashboardPageLayout, {
            title: "Invoices",
            description: "View and manage your billing invoices",
            children: /*#__PURE__*/ _jsx(DashboardPageLoading, {})
        });
    }
    if (!canViewInvoices) {
        return /*#__PURE__*/ _jsx(DashboardPageLayout, {
            title: "Invoices",
            description: "View and manage your billing invoices",
            children: /*#__PURE__*/ _jsx(DashboardPageSection, {
                className: "flex min-h-[400px] items-center justify-center",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "text-center",
                    children: [
                        /*#__PURE__*/ _jsx(Lock, {
                            className: "mx-auto mb-4 h-12 w-12 text-gray-400"
                        }),
                        /*#__PURE__*/ _jsx("h3", {
                            className: "mb-2 text-lg font-medium text-gray-900",
                            children: "Access Restricted"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-gray-500",
                            children: "You don't have permission to view invoices. Please contact your admin for access."
                        })
                    ]
                })
            })
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsx(DashboardPageLayout, {
            title: "Invoices",
            description: "View and manage your billing invoices",
            children: /*#__PURE__*/ _jsx(DashboardPageSection, {
                className: "rounded-md border border-red-200 bg-red-50 p-4",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "flex",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "ml-3",
                        children: [
                            /*#__PURE__*/ _jsx("h3", {
                                className: "text-sm font-medium text-red-800",
                                children: "Error loading invoices"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "mt-2 text-sm text-red-700",
                                children: /*#__PURE__*/ _jsx("p", {
                                    children: getHttpResponseMessage(error) || "Failed to load invoices"
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "mt-4",
                                children: /*#__PURE__*/ _jsx("button", {
                                    onClick: handleRefresh,
                                    className: "rounded-md bg-red-100 px-2 py-1 text-sm font-medium text-red-800 hover:bg-red-200",
                                    children: "Try Again"
                                })
                            })
                        ]
                    })
                })
            })
        });
    }
    return /*#__PURE__*/ _jsxs(DashboardPageLayout, {
        title: "Invoices",
        description: "View and manage your billing invoices",
        actions: /*#__PURE__*/ _jsxs("button", {
            onClick: handleRefresh,
            disabled: isLoading,
            className: "flex items-center space-x-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50",
            children: [
                /*#__PURE__*/ _jsx(RefreshCw, {
                    className: `h-4 w-4 ${isLoading ? "animate-spin" : ""}`
                }),
                /*#__PURE__*/ _jsx("span", {
                    children: "Refresh"
                })
            ]
        }),
        children: [
            /*#__PURE__*/ _jsxs(DashboardPageSection, {
                className: "grid grid-cols-1 gap-5 sm:grid-cols-2",
                children: [
                    /*#__PURE__*/ _jsx(Card, {
                        icon: /*#__PURE__*/ _jsx(Clock, {
                            className: "h-6 w-6 text-navy-70"
                        }),
                        title: INVOICE_STATUS_LABELS[INVOICE_STATUSES.PENDING],
                        subtitle: formatCurrency(totals.totalPending, summaryCurrency),
                        variant: "default"
                    }),
                    /*#__PURE__*/ _jsx(Card, {
                        icon: /*#__PURE__*/ _jsx(CreditCard, {
                            className: "h-6 w-6 text-navy-70"
                        }),
                        title: INVOICE_STATUS_LABELS[INVOICE_STATUSES.OVERDUE],
                        subtitle: formatCurrency(totals.totalOverdue, summaryCurrency),
                        variant: "default"
                    })
                ]
            }),
            isSuperAdmin && /*#__PURE__*/ _jsxs(DashboardPageSection, {
                className: "rounded-lg border border-navy-20 bg-white p-5 shadow-sm",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("h3", {
                                        className: "text-lg font-medium text-gray-900",
                                        children: "Manual Invoice Generation"
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm text-gray-500",
                                        children: "Run invoice generation for a specific customer and date range to verify billing behavior."
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx("button", {
                                        onClick: ()=>applyMonthPreset(0),
                                        type: "button",
                                        className: "rounded-md border border-gray-200 bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200",
                                        children: "Current Month"
                                    }),
                                    /*#__PURE__*/ _jsx("button", {
                                        onClick: ()=>applyMonthPreset(1),
                                        type: "button",
                                        className: "rounded-md border border-gray-200 bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200",
                                        children: "Next Month"
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_220px]",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "mb-2 block text-sm font-medium text-gray-700",
                                        children: "Date Range"
                                    }),
                                    /*#__PURE__*/ _jsx(DateRangeInputs, {
                                        startDate: parseDateInput(manualPeriodStart),
                                        endDate: parseDateInput(manualPeriodEnd),
                                        onStartDateChange: handleManualStartDateChange,
                                        onEndDateChange: handleManualEndDateChange,
                                        startPlaceholder: "Period start",
                                        endPlaceholder: "Period end"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex flex-col justify-end",
                                children: /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    onClick: handleManualGenerate,
                                    disabled: generate.isPending || !invoiceCustomerId || !manualPeriodStart || !manualPeriodEnd,
                                    className: "rounded-md bg-navy px-4 py-2 text-sm font-medium text-white transition hover:bg-navy-80 disabled:cursor-not-allowed disabled:opacity-50",
                                    children: generate.isPending ? "Generating..." : "Generate Invoices"
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "mt-3 text-xs text-gray-500",
                        children: invoiceCustomerId ? `Generating for customer ${invoiceCustomerId}. The normal scheduler runs on the 1st and bills the previous month.` : "Select a specific organization first. Manual generation is disabled while viewing all organizations."
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(InvoiceFilters, {
                filters: filters,
                onFiltersChange: handleFiltersChange
            }),
            /*#__PURE__*/ _jsx(DashboardPageSection, {
                className: "rounded-lg bg-white shadow",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "px-4 py-5 sm:p-6",
                    children: [
                        /*#__PURE__*/ _jsxs("h3", {
                            className: "mb-4 text-lg font-medium leading-6 text-gray-900",
                            children: [
                                "Invoices ",
                                invoices.length > 0 && `(${invoices.length})`
                            ]
                        }),
                        isLoading ? /*#__PURE__*/ _jsx("div", {
                            className: "flex items-center justify-center py-8",
                            children: /*#__PURE__*/ _jsx(CircularProgress, {})
                        }) : invoices.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                            className: "py-8 text-center",
                            children: [
                                /*#__PURE__*/ _jsx(Receipt, {
                                    className: "mx-auto h-12 w-12 text-gray-400"
                                }),
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "mt-2 text-sm font-medium text-gray-900",
                                    children: "No invoices found"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "mt-1 text-sm text-gray-500",
                                    children: "No invoices match your current filters."
                                })
                            ]
                        }) : /*#__PURE__*/ _jsx("div", {
                            className: "space-y-4",
                            children: invoices.map((invoice)=>/*#__PURE__*/ _jsx(InvoiceCard, {
                                    invoice: invoice,
                                    onViewDetails: (inv)=>setSelectedInvoiceId(inv.invoiceId),
                                    onDownloadPdf: (inv)=>download.mutate({
                                            invoiceId: inv.invoiceId,
                                            customerId: inv.customerId || invoiceCustomerId,
                                            forceRegenerate: true
                                        }),
                                    isDownloading: download.isPending,
                                    isSuperAdmin: isSuperAdmin,
                                    onStatusChange: (inv, status)=>updateStatus.mutate({
                                            invoiceId: inv.invoiceId,
                                            status,
                                            customerId: invoiceCustomerId
                                        }),
                                    isUpdatingStatus: updateStatus.isPending
                                }, invoice.invoiceId))
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(InvoiceDetailModal, {
                invoice: selectedInvoice,
                isOpen: selectedInvoice !== null,
                onClose: handleCloseModal,
                isSuperAdmin: isSuperAdmin,
                onStatusChange: (inv, status)=>updateStatus.mutate({
                        invoiceId: inv.invoiceId,
                        status,
                        customerId: invoiceCustomerId
                    }),
                isUpdatingStatus: updateStatus.isPending,
                onDownloadInvoice: (inv)=>download.mutate({
                        invoiceId: inv.invoiceId,
                        customerId: inv.customerId || invoiceCustomerId,
                        forceRegenerate: true
                    }),
                isDownloading: download.isPending
            })
        ]
    });
}
